import axios from "axios";
import store from "./Redux/store";
import { API_URL } from "../src/consts";
import { getAccessToken, signOut } from "./utils/authUtils";

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let failedQueue = [];
let refreshFailed = false; // Flag to prevent infinite retry loops
let retryCount = 0; // Track retry attempts
const MAX_RETRY_ATTEMPTS = 1; // Maximum number of retry attempts (reduced to prevent loops)
let isRedirectingToLogin = false; // Prevent multiple redirects

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Set default base URL for axios
axios.defaults.baseURL = API_URL;

// Request interceptor - cookies are sent automatically by browser
// SECURITY: No Authorization header - backend reads from httpOnly cookies only
axios.interceptors.request.use(
  async (config) => {
    // CRITICAL: Stop all requests if authentication has failed to prevent infinite loops
    if (refreshFailed || isRedirectingToLogin) {
      return Promise.reject(new Error('Authentication failed - redirecting to login'));
    }
    
    // Ensure credentials are included for cookie-based authentication
    config.withCredentials = true;
    
    // Update Redux state if we have a token (for UI state only)
    const state = store.getState();
    if (!state.user?.token) {
      try {
        const token = await getAccessToken();
        if (token) {
          store.dispatch({ type: "SET_TOKEN", payload: token });
        }
      } catch (error) {
        // Not authenticated - cookies will be missing, backend will reject
        // Don't block the request, let it fail naturally
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Enhanced response interceptor with token refresh and loop prevention
axios.interceptors.response.use(
  (response) => {
    // Reset retry count on successful response
    retryCount = 0;
    refreshFailed = false;
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Prevent infinite loops - if refresh already failed, don't retry
    if (refreshFailed) {
      console.warn('Authentication failed - refresh already attempted, stopping retries');
      store.dispatch({ type: "SET_UNAUTHORIZED" });
      return Promise.reject(error);
    }

    // Prevent infinite loops - limit retry attempts
    if (retryCount >= MAX_RETRY_ATTEMPTS) {
      console.warn('Maximum retry attempts reached, stopping retries');
      refreshFailed = true;
      store.dispatch({ type: "SET_UNAUTHORIZED" });
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => {
          // SECURITY: Cookies are sent automatically, no Authorization header needed
          originalRequest.withCredentials = true;
          return axios(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;
      retryCount++;

      try {
        // Attempt to refresh token using the global refresh function
        if (window.refreshAccessToken) {
          await window.refreshAccessToken();
          
          // SECURITY: Cookies are sent automatically after refresh, no Authorization header needed
          originalRequest.withCredentials = true;
          
          // Process any queued requests
          processQueue(null, null);
          
          // Retry the original request (cookies will be sent automatically)
          try {
            const retryResponse = await axios(originalRequest);
            
            // If retry succeeds, reset counters
            retryCount = 0;
            refreshFailed = false;
            
            return retryResponse;
          } catch (retryError) {
            // If retry still fails with 401, backend is not reading cookies correctly
            // Stop retrying to prevent infinite loop
            if (retryError.response?.status === 401) {
              console.error('Retry after refresh still returned 401 - backend may not be reading cookies correctly');
              refreshFailed = true;
              store.dispatch({ type: "SET_UNAUTHORIZED" });
              
              // Redirect to login
              if (window.location.pathname !== '/login' && !isRedirectingToLogin) {
                isRedirectingToLogin = true;
                setTimeout(() => {
                  window.location.href = '/login';
                }, 100);
              }
              
              return Promise.reject(retryError);
            }
            // For other errors, reject normally
            throw retryError;
          }
        } else {
          throw new Error('Refresh function not available');
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        
        // Mark refresh as failed to prevent infinite loops
        refreshFailed = true;
        
        // Process queue with error
        processQueue(refreshError, null);
        
        // SECURITY: Clear all Cognito cookies and sign out
        // This prevents accumulation of old user cookies
        try {
          await signOut(); // Clears all Cognito cookies for all users
        } catch (signOutError) {
          console.error('Error during sign out:', signOutError);
        }
        
        // Clear tokens from Redux
        store.dispatch({ type: "SET_UNAUTHORIZED" });
        
        // Redirect to login if not already there (only once)
        if (window.location.pathname !== '/login' && !isRedirectingToLogin) {
          isRedirectingToLogin = true;
          // Use setTimeout to prevent blocking
          setTimeout(() => {
            window.location.href = '/login';
          }, 100);
        }
        
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // For other errors or if retry failed, reject normally
    return Promise.reject(error);
  }
);

// Export default axios instance (now configured)
export default axios;
